import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";
import { MAP_COLUMNS, MAP_GRAPHS } from "utils/constants";


// Esta función se asegura de que haya espacio suficiente antes de agregar contenido
const ensurePageBreak = (doc, top, pageHeight, marginBottom = 5) => {
    // Verifica si el contenido cabe en la página actual antes de añadirlo
    if (top + marginBottom > pageHeight) {
        doc.addPage();
        return 20;  // Vuelve a establecer la posición 'top' al inicio de la nueva página
    }
    return top;  // Si no hay suficiente espacio, no se agrega salto de página
};

function createPdf({ doc, elements }, datasetColumns, studentActivity, activity, comment, deliveryDate) {
    let top = 20;
    const padding = 10;
    const staticDataSource = activity?.static_data_source;
    const nameDataset = staticDataSource?.name || '';
    const nameActivity = activity?.title;
    const student = `${studentActivity?.student.first_name || 'Anónimo'} ${studentActivity?.student.last_name || 'Anónimo'}`;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const xOffset =
        pageWidth / 2 -
        (doc.getStringUnitWidth("Entrega") * doc.internal.getFontSize()) / 2;
    doc.text("Entrega", xOffset, top);
    top += 20;

    const addLabelValueBlock = (label, value) => {
        top = ensurePageBreak(doc, top, pageHeight);
        doc.setFont(undefined, "bold");
        doc.text(label, padding, top);
        top += 15;
        doc.setFont(undefined, "normal");
        const splitText = doc.splitTextToSize(value, pageWidth - 2 * padding);
        doc.text(splitText, padding + 15, top);
        top += splitText.length * doc.getLineHeight() + 10;
    };

    // Agrega la información estática
    if (staticDataSource) {
        if (deliveryDate !== '') {
            addLabelValueBlock("Fecha de Entrega", deliveryDate);
        }
        addLabelValueBlock("Nombre Dataset", nameDataset);
        addLabelValueBlock(MAP_COLUMNS.datasetColumns || '', datasetColumns?.toString() || '');
        addLabelValueBlock("Título Actividad", nameActivity);
        addLabelValueBlock("Estudiante", student);
        addLabelValueBlock("Enunciado", activity?.statement_title || '');
        addLabelValueBlock("Descripción de la Actividad", activity?.description || '');
    } else {
        if (deliveryDate !== '') {
            addLabelValueBlock("Fecha de Entrega", deliveryDate);
        }
        addLabelValueBlock("Título Actividad", nameActivity);
        addLabelValueBlock("Estudiante", student);
        addLabelValueBlock("Enunciado", activity?.statement_title || '');
        addLabelValueBlock("Descripción de la Actividad", activity?.description || '');
    }

    // Procesa los elementos y gráficos
    elements.forEach((el) => {
        let elHeight = el.offsetHeight;
        let elWidth = el.offsetWidth;

        const scaleFactor = 0.6;
        elHeight = elHeight * scaleFactor;
        elWidth = elWidth * scaleFactor;

        doc.addPage('a3', 'landscape');
        top = 20;
        doc.setFont(undefined, "bold");
        doc.text(MAP_GRAPHS[el.graph], padding, top);
        top += 20;

        const availableWidth = doc.internal.pageSize.getWidth();
        const xCentered = (availableWidth - elWidth) / 2;
        doc.addImage(el.image, "PNG", xCentered, top, elWidth, elHeight);
        top += elHeight + 10;

        // Observaciones para cada gráfico
        if (el.comment) {
            top = ensurePageBreak(doc, top, pageHeight);
            doc.setFont(undefined, "bold");
            doc.text("Observaciones compartidas sobre el gráfico:", padding, top);
            top += 15;
            doc.setFont(undefined, "normal");
            const splitComment = doc.splitTextToSize(el.comment, pageWidth - 2 * padding);
            doc.text(splitComment, padding, top);
            top += splitComment.length * doc.getLineHeight() + 10;
        }

        if (el.columns) {
            top = ensurePageBreak(doc, top, pageHeight);
            top += 10;
            let paddingKeys = padding;

            Object.entries(el.columns).forEach(([k, v]) => {
                if (v && v.length > 0 && k !== "datasetColumns") {
                    const label = MAP_COLUMNS[k];
                    const value = v.toString();

                    if (paddingKeys + 200 > pageWidth) {
                        paddingKeys = padding;
                        top += 40;
                    }

                    top = ensurePageBreak(doc, top, pageHeight);
                    doc.setFont(undefined, "bold");
                    doc.text(label, paddingKeys, top);
                    doc.setFont(undefined, "normal");
                    const splitVal = doc.splitTextToSize(value, pageWidth - paddingKeys - 15);
                    doc.text(splitVal, paddingKeys + 15, top + 10);
                    top += splitVal.length * doc.getLineHeight() + 10;
                }
            });

            const addFilterBlock = (label, filters) => {
                if (filters.length > 0) {
                    top = ensurePageBreak(doc, top, pageHeight);
                    doc.setFont(undefined, "bold");
                    doc.text(label, padding, top);
                    top += 15;
                    doc.setFont(undefined, "normal");
                    filters.forEach((f) => {
                        const lineHeight = doc.getLineHeight();
                        if (top > pageHeight + 40) {
                            doc.addPage();
                            top = 20;
                        }
                        doc.text(`• ${f.label}`, padding + 15, top);
                        top += lineHeight;
                    });
                }
            };

            addFilterBlock("Filtrar por", el.filterInclude);
            addFilterBlock("Excluir por", el.filterExclude);
        }

        if (el.graph.includes('social')) {
            const addFilterBlock = (label, filters) => {
                if (filters.length > 0) {
                    top = ensurePageBreak(doc, top, pageHeight);
                    doc.setFont(undefined, "bold");
                    doc.text(label, padding, top);
                    top += 15;
                    doc.setFont(undefined, "normal");
                    filters.forEach((f) => {
                        const lineHeight = doc.getLineHeight();
                        if (top > pageHeight + 40) {
                            doc.addPage();
                            top = 20;
                        }
                        doc.text(`• ${f.label}`, padding + 15, top);
                        top += lineHeight;
                    });
                }
            };

            addFilterBlock("Filtrar por", el.filterInclude);
            addFilterBlock("Excluir por", el.filterExclude);
        }
    });

    // Observaciones finales
    doc.setFont(undefined, "bold");
    const finalCommentTitle = "Observaciones de la entrega";
    const finalCommentText = comment || "Sin observación.";
    const splitFinalComment = doc.splitTextToSize(finalCommentText, pageWidth - 2 * padding);

    top += 20;
    // Usamos un alto fijo si el método getLineHeight no es fiable

    const lineHeight = 14;
    const finalBlockHeight = 20 + splitFinalComment.length * lineHeight;

    if (top + finalBlockHeight > pageHeight + 40) {
        doc.addPage();
        top = 20;
    }

    // Título
    doc.text(finalCommentTitle, padding, top);
    top += 20;

    // Texto
    doc.setFont(undefined, "normal");
    splitFinalComment.forEach((line) => {
        doc.text(line, padding, top);
        top += lineHeight;
    });
}

export function exportMultipleChartsToPdf(elements, datasetColumns, studentActivity, activity, comment, deliveryDate = '') {
    const doc = new jsPDF("p", "px");
    createPdf({ doc, elements }, datasetColumns, studentActivity, activity, comment, deliveryDate);
    window.open(doc.output("bloburl"));
}

export const exportGraphToImg = async (el) => {
    return await htmlToImage.toPng(el);
};
